import {
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Options } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';
import {
  FormField,
  FormFieldState,
  FormType
} from '@rippling/utils';
import {
  fetchUserData, getClearbitData, getEnrichmentFieldPrefix
} from '@rippling/utils/clearbit';
import {
  FastTrackFormType,
  FastTrackType,
  getFastTrackData,
  handleFastTrack
} from '@rippling/utils/fasttrack';
import {
  GAEventName, getGASessionId, sendGAEvent
} from '@rippling/utils/gaUtils';
import { getVisitorsExperiments } from '@rippling/utils/optimizely/helpers';
import { isStringArray } from '@rippling/utils/typeUtils';
import { getCleanedUrl, getCurrentFormFillLandingPageUrl } from '@rippling/utils/urlUtils';
import { isString } from '@rippling/utils/validationUtils';
import clsx from 'clsx';
import { twJoin } from 'tailwind-merge';
import Cookies from 'universal-cookie';

import useFormAdvancedConfiguration, { type AdvancedConfiguration, type AdvancedConfigurationSubscribeData } from '../../hooks/useFormAdvancedConfiguration';
import useSchedulerFlow from '../../hooks/useSchedulerFlow';
import { SavaContext } from '../../providers/SavaProvider';
import { SchedulerFlowType } from '../../types';
import {
  type BaseCfComponent,
  type CfItem,
  type CfLocale,
  type FormData,
  FormFieldType,
  SurfaceLevel
} from '../../types';
import Alert from '../atoms/Alert';
import Button, { type ButtonProps } from '../atoms/Button';
import Link from '../atoms/Link';
import Scheduler, { getSchedulerFlowDefaultAttributes } from '../atoms/Scheduler';
import CfDebugger from '../CfDebugger';
import CfRichText from '../CfRichText';
import Icon from '../Icon/Icon';
import FormFieldComponent, { type FormFieldProps } from './FormField';

type FastTrackLocales = CfLocale[];

const cookies = new Cookies();

const preferenceCenterFormDataDefaults = {
  Benefits__c: 'false',
  Directmail_Email_Opt_In__c: 'false',
  Email__c: '',
  Email_Type__c: '',
  Finance__c: 'false',
  Global_Payroll__c: 'false',
  HR__c: 'false',
  In_Person_Event_invites__c: 'false',
  IT_Application_and_Device_Management__c: 'false',
  New_Feature_Announcements__c: 'false',
  Newsletter__c: 'false',
  Opt_Out_of_all_Communications__c: 'false',
  Product_Recommendations__c: 'false',
  Snooze_Duration__c: '',
  Surveys__c: 'false',
  Talent__c: 'false',
  Tips_Content__c: 'false',
  Webinars_Invites__c: 'false',
};

const options: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => {
      return (
        <Link
          className="hover:text-text-hyperlink text-text-primary underline underline-offset-4 duration-300 ease-in-out"
          disableDefaultStyle
          href={node.data.uri}
        >
          {children}
        </Link>
      );
    },
  },
};

export interface FormProps extends BaseCfComponent {
  fields: {
    advancedConfiguration: AdvancedConfiguration;
    buttonText: string;
    campaignId: string;
    confirmationUrl: string;
    disclaimer: CfItem;
    enableFastTrack?: boolean;
    enableFormEnrichment?: boolean;
    enableRtlid?: boolean;
    fastTrackLocales?: FastTrackLocales;
    fastTrackType?: FastTrackType;
    fields: FormFieldProps[];
    formType: FormType;

    /**
     * If true, will check if there is only 1 field, the button will be displayed inline
     */
    integratedCta?: boolean;

    // Demotime booking
    isSchedulerEnabled?: boolean;
    schedulerCountries?: string[];
    schedulerFlowType?: SchedulerFlowType;
    schedulerMaximumNumberOfEmployees?: number;
    schedulerMinimumNumberOfEmployees?: number;
    schedulerRestrictByCountryPicked?: boolean;
    schedulerRestrictByNumberOfEmployees?: boolean;
    schedulerThankYouPageUrl?: string;

    subtitle?: string
    successMessage?: string;
    surfaceLevel?: SurfaceLevel,
    systemTitle: string;
    title?: string;
  }

  // Callback to be called after the form is submitted
  onPostSubmit?: () => void;
  popupId?: string;
}

const isFieldEnriched = (fieldName: string, formFieldState: FormFieldState) => {
  return formFieldState[`${getEnrichmentFieldPrefix()}${fieldName}`] === true;
};

const shouldRender = (
  { fields: { name, type } }: FormFieldProps,
  formFieldState: FormFieldState,
  forceHide: boolean
) => {
  if (isFieldEnriched(name, formFieldState)) {
    return false;
  }

  if (type === FormFieldType.Hidden) {
    return false;
  }

  if (name === FormField.State) {
    return formFieldState[FormField.Country] === 'United States';
  }

  if (forceHide) {
    return false;
  }

  // Always render
  return true;
};

// All keys to be excluded from the form data
const getFieldsToBeExcludedFromFormData = ({ fields, formFieldState }: {
  fields: FormFieldProps[],
  formFieldState: FormFieldState
}) => {
  const fieldKeysToSkip = fields
    .filter(({ fields: { appendToComment } }) => appendToComment)
    .map(({ fields: { name } }) => name);

  const enrichmentFields = Object.keys(formFieldState).filter((key) => {
    return key.startsWith(getEnrichmentFieldPrefix());
  });

  return new Set([...fieldKeysToSkip, ...enrichmentFields]);
};

// Get data to be appended to the comment
const getDataToBeAppendedToComment = ({ fields, formFieldState }: {
  fields: FormFieldProps[],
  formFieldState: FormFieldState
}) => {
  // All keys to be excluded from the form data
  return fields
    .filter(({ fields: { appendToComment } }) => appendToComment)
    .map(({ fields: { label, name } }) => {
      return `${label || name}: ${formFieldState[name] ?? '(not provided)'}`;
    })
    .join('\n');
};

const sendFormEvent = (
  event: GAEventName.FormError | GAEventName.FormStarted,
  formType: FormType,
  popupId?: string
) => {
  sendGAEvent({
    event,
    form_type: formType,
  });

  if (popupId) {
    sendGAEvent({
      event: event === GAEventName.FormError ? GAEventName.PopupFormError : GAEventName.PopupFormStart,
      form_type: formType,
      popup_name: popupId,
    });
  }
};

const handlePreferenceCenterInputChange = (
  name: string,
  value: string | string[],
  formFieldState: FormFieldState
): FormFieldState => {
  // TODO: consider moving to advanced configuration
  const unsubscribeKey = 'user_email_preferences_unsubscribe_1';
  const unsubscribeValue = 'Opt_Out_of_all_Communications__c';

  // If the user is unsubscribing from all, clear all fields except for email and unsubscribe
  if (name === unsubscribeKey && value?.includes(unsubscribeValue)) {
    return Object.entries(formFieldState).reduce<FormFieldState>((acc, [key, val]) => {
      // handle array values (multi-select)
      if (Array.isArray(val) && key !== unsubscribeKey) {
        acc[key] = [];
      }

      // handle string values (single-select/radio)
      if (key !== FormField.Email && typeof val === 'string') {
        acc[key] = '';
      }

      return acc;
    }, { [name]: value });

  // If the user is subscribing to any or all, clear the unsubscribe field
  } else if (name !== unsubscribeKey) {
    return {
      [name]: value,
      user_email_preferences_unsubscribe_1: [],
    };
  }

  return { [name]: value };
};

function breakUpSubscribeData(formData: FormData, dataMap: AdvancedConfigurationSubscribeData) {
  const submissions = [];

  for (const key in dataMap) {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      const { llo, type } = dataMap[key];
      const submission = {
        ...Object.fromEntries(Object.entries(formData).filter(([formKey]) =>
          !Object.prototype.hasOwnProperty.call(dataMap, formKey))),
        Form_Type__c: type,
        [key]: formData[key],
        Last_Lead_Origin__c: llo,
      };

      submissions.push(submission);
    }
  }

  return submissions;
}

export const Form = ({
  className,
  fields: {
    advancedConfiguration,
    buttonText,
    campaignId,
    confirmationUrl,
    disclaimer,
    enableFastTrack,
    enableFormEnrichment = false,

    //fastTrackLocales,

    enableRtlid = false,
    fastTrackType = FastTrackFormType.PAYROLL,
    fields,
    formType,
    integratedCta = false,
    isSchedulerEnabled = false,
    schedulerCountries,
    schedulerFlowType,
    schedulerMaximumNumberOfEmployees,
    schedulerMinimumNumberOfEmployees,
    schedulerRestrictByCountryPicked = false,
    schedulerRestrictByNumberOfEmployees = false,
    schedulerThankYouPageUrl = '',
    successMessage,
    surfaceLevel = SurfaceLevel.L1,
    title,
  },
  onPostSubmit,
  popupId,
  sys,
}: FormProps) => {
  const {
    additionalFormData,
    advancedConfigurationPostSubmitData,
    advancedConfigurationSubscribeData,
    advancedConfigurationSystemData,
    remapToOpenpriseKeysFn,
  } = useFormAdvancedConfiguration(advancedConfiguration);

  const [formFieldState, setFormFieldState] = useState<FormFieldState>(() => {
    return fields.reduce((acc, {
      fields: {
        defaultValue, name, type,
      },
    }) => {
      if (type === FormFieldType.Hidden) {
        return {
          ...acc,
          [name]: defaultValue,
        };
      }

      return acc;
    }, {});
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [schedulerPayload, setSchedulerPayload] = useState({});
  const [isSchedulerEligible, setIsSchedulerEligible] = useState(false);
  const contextValue = useContext(SavaContext);
  const formStarted = useRef(false);
  const { googleClientId, rtlid = '' } = contextValue ?? {};
  const shouldForceHideFields = !!(enableRtlid && rtlid);
  const isPreferenceCenterFlow = formType === FormType.PreferenceCenter;
  const successMessageRef = useRef<HTMLDivElement | null>(null);

  const existingEmail = useMemo(() => {
    // Coming from email-only flow
    return cookies.get('user_email') ?? '';
  }, []);

  if (!contextValue) {
    // @TODO -- log
    return;
  }

  const {
    redirect, schedulerBaseUrl = '', submitForm,
  } = contextValue;

  const { renderableFields } = useMemo(() => {
    return {
      numberOfEmployees: fields.find(({ fields: { name } }) => name === FormField.NumberOfPeople),
      renderableFields: fields.filter((field) => shouldRender(field, formFieldState, shouldForceHideFields)),
    };
  }, [fields, formFieldState]);

  const inlineCtaProps = useMemo<ButtonProps | undefined>(() => {
    if (!integratedCta || renderableFields.length !== 1) {
      return;
    }

    return {
      children: buttonText,
      type: 'submit',
    };
  }, [
    buttonText,
    integratedCta,
    renderableFields,
  ]);

  useEffect(() => {
    if (isSubmitted && successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        // @TODO de we prefer smooth scrolling or jump?
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [isSubmitted]);

  useEffect(() => {
    if (!existingEmail) {
      return;
    }

    // prepopulate email field
    setFormFieldState((prevFieldStates) => ({
      ...prevFieldStates,
      [FormField.Email]: existingEmail,
    }));

    // Form enrichment
    if (!enableFormEnrichment) {
      return;
    }

    (async () => {
      const cbData = await fetchUserData({ email: existingEmail });

      if (!cbData) {
        // Do nothing
        return;
      }

      /**
       * Enrichment fields are set to true to indicate that the field has been enriched.
       * This is used to determine if the field should be rendered or not.
       * Fields prefixed with "enriched_" (generated by getEnrichmentFieldName)
       * are filtered out from the form data posted to the backend.
       *
       * This approach simplifies state management by keeping enrichment data
       * together with the form fields, thus avoiding the need for multiple state objects.
       *
       * Example:
       * {
       *   'enriched_fieldNameA': true,
       *   'enriched_fieldNameB': true,
       *   'fieldNameA': 'value',
       *   'fieldNameB': 'value',
       *   ...otherFields
       * }
       */
      setFormFieldState((prevFieldStates) => {
        const updates: { [key: string]: boolean | string } = {};

        if (cbData.name) {
          updates[FormField.CompanyName] = cbData.name;
          updates[`${getEnrichmentFieldPrefix()}${FormField.CompanyName}`] = true;
        }

        if (cbData.country) {
          updates[FormField.Country] = cbData.country;
          updates[`${getEnrichmentFieldPrefix()}${FormField.Country}`] = true;
        }

        return {
          ...prevFieldStates,
          ...updates,
        };
      });

      window?.dataLayer?.push({
        Country: cbData.country ? true : false,
        event: 'clearbit_form_enrichment',
        user_company: cbData.name ? true : false,
        user_email: true,
      });
    })();
  }, [existingEmail]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!submitForm) {
      // @TODO -- log
      return;
    }

    setIsSubmitting(true);

    let thankYouUrl = getCleanedUrl(confirmationUrl);
    const cbRevealData = getClearbitData();

    // All keys to be excluded from the form data
    const fieldKeysToSkip = getFieldsToBeExcludedFromFormData({
      fields: renderableFields,
      formFieldState,
    });

    // Form data to be sent to backend
    const formData: FormData = Object.keys(formFieldState).reduce(
      (accFormData, name) => {
        // Skip fields
        if (fieldKeysToSkip.has(name)) {
          return accFormData;
        }

        if (isStringArray(formFieldState[name])) {
          const values = formFieldState[name].reduce((acc, key) => {
            return {
              ...acc,
              [key]: 'true',
            };
          }, {});

          return {
            ...accFormData,
            ...values,
          };
        }

        if (name === FormField.FullName && isString(formFieldState[name])) {
          const nameStrings = formFieldState[name].split(/(?<=^\S+)\s/);

          return {
            ...accFormData,
            FirstName: nameStrings[0],
            LastName: nameStrings[1],
          };
        }

        return {
          ...accFormData,
          [name]: formFieldState[name],
        };
      },
      {
        Append_to_Comments__c: getDataToBeAppendedToComment({
          fields: renderableFields,
          formFieldState,
        }),

        Company: '',
        Contact_ID: enableRtlid ? rtlid : undefined,
        Country: '',
        Email: '',
        FirstName: '',
        Form_Type__c: formType,
        formFillLandingPage: getCurrentFormFillLandingPageUrl(),
        ga_session_id: getGASessionId({ gaSessionKey: process.env.NEXT_PUBLIC_GA_SESSION_KEY }),
        GA4_Client_ID__c: googleClientId,
        Last_Lead_Origin__c: campaignId,
        LastName: '',
        ...additionalFormData,
        Optimizely_Variation__c: getVisitorsExperiments(),
        user_agent: navigator.userAgent || '',
        visitor_ip: cbRevealData.ip,
        ...isPreferenceCenterFlow ? preferenceCenterFormDataDefaults : {},
        ...getSchedulerFlowDefaultAttributes(),
      }
    );

    if (enableFastTrack) {
      const isFastTrackSuccessful = await handleFastTrack({
        country: formData.Country,
        email: formData.Email,
        fastTrackType,
        formFieldState,
        formType,
      });

      if (isFastTrackSuccessful) {
        const { llo, redirectUrl } = getFastTrackData(fastTrackType);

        confirmationUrl = redirectUrl;
        formData.Last_Lead_Origin__c = llo;

        // Disable scheduler flow if fast track eligible
        if (isSchedulerEnabled) {
          isSchedulerEnabled = false;
        }
      }
    }

    const isSchedulerFlow = isSchedulerEnabled && useSchedulerFlow({
      schedulerBaseUrl,
      schedulerCountries,
      schedulerFlowType,
      schedulerMaximumNumberOfEmployees,
      schedulerMinimumNumberOfEmployees,
      schedulerRestrictByCountryPicked,
      schedulerRestrictByNumberOfEmployees,
      values: formData as FormFieldState,
    });

    if (isSchedulerFlow) {
      /**
       * In the scheduler flow we send two form submits:
       * 1. The first submit is will be just data collected from the form
       * 2. The second submit will be the data collected from the form + the scheduler data
       *    - This second submit will handle the TY page redirect
       *    - Setting this to empty string to prevent the form from redirecting
       */
      thankYouUrl = '';

      formData.demotimeEligible = true;

      // trigger scheduler modal
      setIsSchedulerEligible(true);
      setSchedulerPayload(formData);
    }

    try {
      /**
       * Determine the submissions array based on the presence of advanced subscribe data.
       *
       * - If `advancedConfigurationSubscribeData` is present, break up the `formData` into multiple submissions.
       * - Otherwise, default to a single submission containing the entire `formData`.
       *
       * Note: This approach is necessary due to current project deadlines and limitations in the backend system.
       * We have plans to update the backend to handle submissions more efficiently in the future.
       *
       * Jira Tickets
       * - https://rippling.atlassian.net/browse/WWW-1268
       * - https://rippling.atlassian.net/browse/WWW-1272
       */
      const submissions = advancedConfigurationSubscribeData
        ? breakUpSubscribeData(formData, advancedConfigurationSubscribeData)
        : [formData];

      await Promise.all(submissions.map((submission) => {
        return submitForm({
          formData: submission as FormData,
          postSubmitData: advancedConfigurationPostSubmitData,
          remapToOpenpriseKeysFn,
          systemData: advancedConfigurationSystemData,
          thankYouUrl: confirmationUrl,
        });
      }));

      setIsSubmitted(true);

      if (popupId) {
        sendGAEvent({
          event: GAEventName.PopupFormSubmit,
          form_type: formType,
          popup_name: popupId,
        });
      }

      onPostSubmit?.();

      // Redirect should happen last
      if (thankYouUrl) {
        if (thankYouUrl.startsWith('/')) {
          // Internal url
          redirect?.({
            pathname: thankYouUrl,
            query: { sava: 1 },
          });

          return;
        }

        window.location.href = thankYouUrl;

        return;
      }

      setIsSubmitting(false);
    } catch (error) {
      console.error('Error submitting form:', error);

      // TODO: update form state to show error message
    }
  };

  const handleInputChange = (name: string, value: string | string[]) => {
    /**
     * Manages the "unsubscribe from all" scenario by clearing all fields
     * except for the email and unsubscribe fields, and handles the reverse
     * operation when necessary.
     */
    if (isPreferenceCenterFlow) {
      const stateUpdate = handlePreferenceCenterInputChange(name, value, formFieldState);

      setFormFieldState((prevFieldStates) => ({
        ...prevFieldStates,
        ...stateUpdate,
      }));

      return;
    }

    // Default state update logic
    setFormFieldState((prevFieldStates) => ({
      ...prevFieldStates,
      [name]: value,
    }));
  };

  const handleInvalidInput = () => {
    sendFormEvent(GAEventName.FormError, formType, popupId);
  };

  const handleFormStart = () => {
    if (formStarted.current) {
      return;
    }
    sendFormEvent(GAEventName.FormStarted, formType, popupId);
    formStarted.current = true;
  };

  return (
    <CfDebugger
      className={twJoin(
        'flex flex-col gap-sm w-full',
        surfaceLevel === SurfaceLevel.L0 ? 'max-w-md' : 'p-xl bg-surface-l1 rounded-md max-w-sm',
        className
      )}
      sys={sys}
    >
      {!inlineCtaProps && !!title && (
        <span className="text-h3 text-text-primary text-center font-bold">
          {title}
        </span>
      )}

      {isSubmitted && isSchedulerEligible && (
        <Scheduler
          failRedirectUrl={confirmationUrl}
          formData={schedulerPayload}
          formType={formType}
          isSchedulerEligible={isSchedulerEligible}
          schedulerFlowType={schedulerFlowType}
          successRedirectUrl={schedulerThankYouPageUrl}
        />
      )}

      {isSubmitted && successMessage ? (
        <div ref={successMessageRef}>
          <Alert message={successMessage} variant="success" />
        </div>
      ) : (
        <>
          <form
            className="flex flex-col gap-3 text-left"
            onClick={handleFormStart}
            onSubmit={handleSubmit}
          >
            {renderableFields.map((field) => {
              return (
                <FormFieldComponent
                  {...field}
                  key={field.sys.id}
                  buttonProps={inlineCtaProps}
                  onChange={handleInputChange}
                  onInvalid={handleInvalidInput}
                  surfaceLevel={surfaceLevel}
                  value={formFieldState[field.fields.name]}
                />
              );
            })}
            <Button
              className={clsx(
                'mt-2 lg:mt-3 w-full',
                inlineCtaProps && 'block sm:hidden'
              )}
              disabled={isSubmitting}
              type="submit"
            >
              {buttonText}
            </Button>
          </form>
        </>
      )}

      {disclaimer && (
        <CfDebugger className="text-text-primary text-p2 flex flex-col gap-lg" sys={disclaimer.sys}>
          <div className="flex items-start gap-xs text-caption text-text-secondary">
            {disclaimer.fields.iconFile && (
              <Icon
                assetFile={disclaimer.fields.iconFile}
                className="[&>svg]:w-full lg:[&>svg]:w-full [&>svg]:fill-icon-inactive [&>svg]:h-auto lg:[&>svg]:h-auto"
                containerClassName="w-4 h-4 shrink-0 m-1"
                disableDefaultStyle
              />
            )}
            {disclaimer.fields.body && (
              <CfRichText
                className="text-text-highlight"
                document={disclaimer.fields.body}
                options={options}
              />
            )}
          </div>
        </CfDebugger>
      )}
    </CfDebugger>
  );
};

export default Form;
